import { css, type CSSResult } from 'lit';

/**
 * The rules that make `<fm-api-docs>` its own scroll container, injected into the
 * enclosing shadow root next to the dark override because the page renders into
 * the light DOM and has no `static styles` of its own.
 *
 * Inside the application frame the page is its own scroll container, the one
 * exception to `docs/design/pages.md` → *Page Container*. Swagger-UI virtualizes
 * its operations list from 150 rows on and looks for the scroll container by
 * walking `parentElement` for an `overflow` of `auto` or `scroll`. The frame's
 * `main` sits in a shadow root the page reaches only through its slot, so the
 * walk ends at the frame host and Swagger-UI falls back to watching the window,
 * which never scrolls here: it renders one window height of operations and
 * leaves the rest of the list blank. A scroll container on the light-DOM parent
 * of `#swagger-ui` is what the walk can find.
 *
 * Once the page declares `data-scrolls-itself`, the main area turns into a flex
 * column without padding that clips instead of scrolling, so the page grows to
 * fill it, carries the main area's padding tokens itself and reserves the
 * scrollbar gutters the main area gave up: the documentation starts under the
 * header and its scrollbar sits at the outer edge like on every other page.
 * `position: relative` makes the page the `offsetParent` Swagger-UI measures
 * the list's offset against, and `tabindex="-1"` lets a click into the page
 * take the focus the keys scroll by, without a ring, like the main area it
 * stands in for.
 * Scoped to the frame: in the public shell the window scrolls, and Swagger-UI
 * finds that on its own.
 */
export const apiDocsScrollerStyles: CSSResult = css`
    fm-api-docs {
        display: block;
    }

    fm-application-frame > fm-api-docs {
        position: relative;
        flex: 1;
        min-height: 0;
        padding:
            var(--fm-main-content-padding-top)
            var(--fm-main-content-padding-inline)
            var(--fm-main-content-padding-bottom);
        overflow-y: auto;
        scrollbar-gutter: stable both-edges;
    }

    fm-application-frame > fm-api-docs:focus {
        outline: none;
    }
`;
